/**
 * Pipeline funnel over every Lead Accelerator stage — a true funnel shape.
 *
 * The silhouette tapers evenly from top to bottom (straight edges, each
 * segment's bottom edge = the next segment's top edge), so it always reads
 * as a funnel; the numbers inside carry the data. Proportional widths made
 * an hourglass whenever two stages had similar counts.
 *
 * Left: stage name. Inside: leads that reached the stage. Right: step
 * conversion from the stage above + how many are in the stage now.
 * funnel: the object from pipelineFunnel().
 */
import { useId } from 'react';
import { fmtInt, pct } from './format';
import './modern.css';

const MIN_WIDTH = 30;   // % — width of the funnel's spout

export default function FunnelViz({ funnel, title = 'Lead → delivered' }) {
  const uid = useId().replace(/:/g, '');
  const stages = funnel?.stages || [];
  const total = funnel?.total || 0;
  if (!total) return <div className="md-empty">No leads yet.</div>;

  const by = Object.fromEntries(stages.map((s) => [s.key, s]));
  const delivered = by.DELIVERED?.reached || 0;
  const overall = pct(delivered, total);
  const tdToWin = pct(delivered, by.TEST_DRIVE?.reached || 0);
  const lost = funnel.lost || 0;
  const n = stages.length;
  const widthAt = (k) => 100 - (k / n) * (100 - MIN_WIDTH);

  return (
    <div className="md-fn">
      <div className="md-fv-summary">
        <div className="md-fv-kpi">
          <MiniRing value={overall} />
          <div><b>{overall}%</b><span>{title}</span></div>
        </div>
        <div className="md-fv-kpi">
          <div><b>{tdToWin}%</b><span>Test drive → delivered</span></div>
        </div>
        <div className="md-fv-kpi lost">
          <div><b>{fmtInt(lost)}</b><span>Lost · {pct(lost, total)}% of leads</span></div>
        </div>
      </div>

      <div className="md-fn-body">
        {stages.map((s, i) => {
          const top = widthAt(i);
          const bottom = widthAt(i + 1);
          const tl = (100 - top) / 2; const tr = (100 + top) / 2;
          const bl = (100 - bottom) / 2; const br = (100 + bottom) / 2;
          const conv = i === 0 ? null : pct(s.reached, stages[i - 1].reached);
          return (
            <div key={s.key} className="md-fn-row" style={{ animationDelay: `${i * 45}ms` }}
              title={`${s.reached} reached ${s.label} · ${s.now} in this stage now`}>
              <span className="md-fn-label">{s.label}</span>
              <span className="md-fn-seg">
                <svg viewBox="0 0 100 30" preserveAspectRatio="none" aria-hidden>
                  <defs>
                    <linearGradient id={`${uid}f${i}`} x1="0" x2="1" y1="0" y2="0">
                      <stop offset="0" stopColor={s.colors[0]} />
                      <stop offset="0.55" stopColor={s.colors[1]} />
                      <stop offset="1" stopColor={s.colors[0]} />
                    </linearGradient>
                    <linearGradient id={`${uid}h${i}`} x1="0" x2="0" y1="0" y2="1">
                      <stop offset="0" stopColor="#fff" stopOpacity="0.22" />
                      <stop offset="0.6" stopColor="#fff" stopOpacity="0" />
                    </linearGradient>
                  </defs>
                  <polygon points={`${tl},0 ${tr},0 ${br},30 ${bl},30`} fill={`url(#${uid}f${i})`} />
                  <polygon points={`${tl},0 ${tr},0 ${br},30 ${bl},30`} fill={`url(#${uid}h${i})`} />
                </svg>
                <b>{fmtInt(s.reached)}</b>
              </span>
              <span className="md-fn-meta">
                {conv == null
                  ? <em>all leads</em>
                  : <span className={`md-fn-conv ${conv >= 50 ? 'good' : conv >= 25 ? 'mid' : 'low'}`}>{conv}%</span>}
                <small>{s.now} here</small>
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function MiniRing({ value = 0, size = 38, stroke = 5 }) {
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const v = Math.max(0, Math.min(100, value));
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} aria-hidden>
      <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="var(--border-color)" strokeWidth={stroke} />
      <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="var(--success)" strokeWidth={stroke}
        strokeLinecap="round" strokeDasharray={`${(v / 100) * c} ${c}`} transform={`rotate(-90 ${size / 2} ${size / 2})`} />
    </svg>
  );
}
